import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { getAgencyColorByAgency, AgencyDot } from "@/lib/agencyColors";
import {
  Settings,
  Building2,
  Users,
  Plus,
  Trash2,
  MapPin,
  Phone,
  Shield,
  Mail,
  User,
  Pencil
} from "lucide-react";

export default function SettingsPage() {
  const { user, agencies, setAgencies } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showAddAgencyDialog, setShowAddAgencyDialog] = useState(false);
  const [showEditAgencyDialog, setShowEditAgencyDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [metaStatus, setMetaStatus] = useState(null);
  const [metaPages, setMetaPages] = useState([]);
  const [simulating, setSimulating] = useState(false);
  
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "manager",
    agency_id: ""
  });

  const [newAgency, setNewAgency] = useState({
    name: "",
    city: "",
    address: "",
    phone: ""
  });

  useEffect(() => {
    if (user?.role === "admin") {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, agenciesRes, smsRes, metaRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/agencies`),
        axios.get(`${API}/sms/status`),
        axios.get(`${API}/meta/status`)
      ]);
      setUsers(usersRes.data);
      setAgencies(agenciesRes.data);
      setSmsStatus(smsRes.data);
      setMetaStatus(metaRes.data);
      if (metaRes.data?.token_valid) {
        axios.get(`${API}/meta/pages`).then(r => setMetaPages(r.data?.pages || [])).catch(() => {});
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      if (!newUser.email || !newUser.password || !newUser.name) {
        toast.error("Veuillez remplir tous les champs obligatoires");
        return;
      }
      
      await axios.post(`${API}/users`, newUser);
      toast.success("Utilisateur créé avec succès");
      setShowAddUserDialog(false);
      fetchData();
      setNewUser({
        email: "",
        password: "",
        name: "",
        role: "manager",
        agency_id: ""
      });
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la création";
      toast.error(message);
    }
  };

  const handleCreateAgency = async () => {
    try {
      if (!newAgency.name || !newAgency.city) {
        toast.error("Veuillez remplir le nom et la ville");
        return;
      }
      
      await axios.post(`${API}/agencies`, newAgency);
      toast.success("Agence créée avec succès");
      setShowAddAgencyDialog(false);
      fetchData();
      setNewAgency({
        name: "",
        city: "",
        address: "",
        phone: ""
      });
    } catch (error) {
      toast.error("Erreur lors de la création de l'agence");
    }
  };

  const handleEditAgency = (agency) => {
    setEditingAgency({ ...agency });
    setShowEditAgencyDialog(true);
  };

  const handleUpdateAgency = async () => {
    try {
      if (!editingAgency.name || !editingAgency.city) {
        toast.error("Veuillez remplir le nom et la ville");
        return;
      }
      
      await axios.put(`${API}/agencies/${editingAgency.id}`, {
        name: editingAgency.name,
        city: editingAgency.city,
        address: editingAgency.address,
        phone: editingAgency.phone,
        meta_page_id: editingAgency.meta_page_id || null,
        meta_form_ids: (editingAgency.meta_form_ids_text ?? (editingAgency.meta_form_ids || []).join(", "))
          .split(",").map(s => s.trim()).filter(Boolean)
      });
      toast.success("Agence modifiée avec succès");
      setShowEditAgencyDialog(false);
      setEditingAgency(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la modification de l'agence");
    }
  };

  const handleDeleteAgency = async (agencyId) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette agence ?")) return;
    
    try {
      await axios.delete(`${API}/agencies/${agencyId}`);
      toast.success("Agence supprimée");
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la suppression";
      toast.error(message);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Utilisateur supprimé");
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la suppression";
      toast.error(message);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    
    const isAssocie = editingUser.role === "associe";
    if (isAssocie && (!editingUser.agency_ids || editingUser.agency_ids.length === 0)) {
      toast.error("Sélectionnez au moins une agence pour l'associé");
      return;
    }
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        name: editingUser.name,
        role: editingUser.role,
        agency_id: (editingUser.role === "manager" || editingUser.role === "mecano") ? editingUser.agency_id || null : null,
        agency_ids: isAssocie ? editingUser.agency_ids : null
      });
      toast.success("Utilisateur modifié avec succès");
      setShowEditUserDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de la modification";
      toast.error(message);
    }
  };

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    return agency?.city || "—";
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="bg-slate-900 border-slate-800 p-8 text-center">
          <Shield className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            ACCÈS RESTREINT
          </h2>
          <p className="text-slate-400">
            Cette page est réservée aux administrateurs.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 
          className="text-2xl sm:text-3xl font-bold text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          PARAMÈTRES
        </h1>
        <p className="text-slate-400 mt-1">
          Gestion des agences et des utilisateurs
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Agencies Section */}
        <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
          <CardHeader className="border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#FEED00]" />
                <CardTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  AGENCES
                </CardTitle>
              </div>
              <Dialog open={showAddAgencyDialog} onOpenChange={setShowAddAgencyDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="btn-primary" data-testid="add-agency-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      NOUVELLE AGENCE
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                      Ajouter une nouvelle agence
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Nom de l'agence *</Label>
                      <Input
                        value={newAgency.name}
                        onChange={(e) => setNewAgency({ ...newAgency, name: e.target.value })}
                        placeholder="Auto-Reprog Narbonne"
                        className="bg-slate-950 border-slate-800 text-slate-100"
                        data-testid="new-agency-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Ville *</Label>
                      <Input
                        value={newAgency.city}
                        onChange={(e) => setNewAgency({ ...newAgency, city: e.target.value })}
                        placeholder="Narbonne"
                        className="bg-slate-950 border-slate-800 text-slate-100"
                        data-testid="new-agency-city"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Adresse</Label>
                      <Input
                        value={newAgency.address}
                        onChange={(e) => setNewAgency({ ...newAgency, address: e.target.value })}
                        placeholder="123 Rue de la République"
                        className="bg-slate-950 border-slate-800 text-slate-100"
                        data-testid="new-agency-address"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Téléphone</Label>
                      <Input
                        value={newAgency.phone}
                        onChange={(e) => setNewAgency({ ...newAgency, phone: e.target.value })}
                        placeholder="04 68 00 00 00"
                        className="bg-slate-950 border-slate-800 text-slate-100"
                        data-testid="new-agency-phone"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowAddAgencyDialog(false)}
                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                      >
                        Annuler
                      </Button>
                      <Button 
                        onClick={handleCreateAgency}
                        className="flex-1 btn-primary"
                        data-testid="confirm-new-agency-btn"
                      >
                        Créer
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Edit Agency Dialog */}
              <Dialog open={showEditAgencyDialog} onOpenChange={setShowEditAgencyDialog}>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      MODIFIER L'AGENCE
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                      Modifier les informations de l'agence
                    </DialogDescription>
                  </DialogHeader>
                  
                  {editingAgency && (
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label className="text-slate-300">Nom de l'agence *</Label>
                        <Input
                          value={editingAgency.name}
                          onChange={(e) => setEditingAgency({ ...editingAgency, name: e.target.value })}
                          className="bg-slate-950 border-slate-800 text-slate-100"
                          data-testid="edit-agency-name"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-slate-300">Ville *</Label>
                        <Input
                          value={editingAgency.city}
                          onChange={(e) => setEditingAgency({ ...editingAgency, city: e.target.value })}
                          className="bg-slate-950 border-slate-800 text-slate-100"
                          data-testid="edit-agency-city"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-slate-300">Adresse</Label>
                        <Input
                          value={editingAgency.address || ""}
                          onChange={(e) => setEditingAgency({ ...editingAgency, address: e.target.value })}
                          className="bg-slate-950 border-slate-800 text-slate-100"
                          data-testid="edit-agency-address"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-slate-300">Téléphone</Label>
                        <Input
                          value={editingAgency.phone || ""}
                          onChange={(e) => setEditingAgency({ ...editingAgency, phone: e.target.value })}
                          className="bg-slate-950 border-slate-800 text-slate-100"
                          data-testid="edit-agency-phone"
                        />
                      </div>

                      <div className="pt-3 mt-1 border-t border-slate-800 space-y-3">
                        <p className="text-xs font-semibold tracking-wide text-[#FEED00]">META LEAD ADS — ROUTAGE DES LEADS</p>
                        <div className="space-y-2">
                          <Label className="text-slate-300">ID de la page Facebook de ce centre</Label>
                          <Input
                            value={editingAgency.meta_page_id || ""}
                            onChange={(e) => setEditingAgency({ ...editingAgency, meta_page_id: e.target.value })}
                            placeholder="ex: 201331056720771"
                            list="meta-pages-list"
                            className="bg-slate-950 border-slate-800 text-slate-100 font-mono"
                            data-testid="edit-agency-meta-page-id"
                          />
                          {metaPages.length > 0 && (
                            <datalist id="meta-pages-list">
                              {metaPages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </datalist>
                          )}
                          <p className="text-xs text-slate-500">Les leads reçus depuis cette page arrivent automatiquement dans ce centre.</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-slate-300">IDs de formulaires Meta <span className="text-slate-500 text-xs">(optionnel, séparés par des virgules)</span></Label>
                          <Input
                            value={editingAgency.meta_form_ids_text ?? (editingAgency.meta_form_ids || []).join(", ")}
                            onChange={(e) => setEditingAgency({ ...editingAgency, meta_form_ids_text: e.target.value })}
                            placeholder="ex: 1234567890, 0987654321"
                            className="bg-slate-950 border-slate-800 text-slate-100 font-mono"
                            data-testid="edit-agency-meta-form-ids"
                          />
                          <p className="text-xs text-slate-500">Utile si plusieurs centres partagent la même page : le formulaire décide du centre.</p>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setShowEditAgencyDialog(false);
                            setEditingAgency(null);
                          }}
                          className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                        >
                          Annuler
                        </Button>
                        <Button 
                          onClick={handleUpdateAgency}
                          className="flex-1 btn-primary"
                          data-testid="confirm-edit-agency-btn"
                        >
                          Enregistrer
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-800">
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="p-4">
                    <div className="h-12 bg-slate-800 rounded skeleton" />
                  </div>
                ))
              ) : agencies.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune agence configurée</p>
                </div>
              ) : (
                agencies.map((agency) => (
                  <div 
                    key={agency.id} 
                    className="p-4 flex items-center justify-between table-row-hover"
                    data-testid={`agency-${agency.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-[#FEED00]" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{agency.name}</p>
                        <p className="text-sm text-slate-500">{agency.city}</p>
                        {agency.address && (
                          <p className="text-xs text-slate-600">{agency.address}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {agency.phone && (
                        <div className="hidden sm:flex items-center gap-2 text-slate-400 text-sm mr-2">
                          <Phone className="w-4 h-4" />
                          {agency.phone}
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEditAgency(agency)}
                        className="text-slate-400 hover:text-[#FEED00] hover:bg-[#FEED00]/10"
                        data-testid={`edit-agency-${agency.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteAgency(agency.id)}
                        className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                        data-testid={`delete-agency-${agency.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Users Section */}
        <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
          <CardHeader className="border-b border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#FEED00]" />
                <CardTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  UTILISATEURS
                </CardTitle>
              </div>
              <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="btn-primary" data-testid="add-user-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
                  <DialogHeader>
                    <DialogTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      NOUVEL UTILISATEUR
                    </DialogTitle>
                    <DialogDescription className="text-slate-400">
                      Ajouter un nouveau manager d'agence
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label className="text-slate-300">Nom complet *</Label>
                      <Input
                        value={newUser.name}
                        onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder="Jean Dupont"
                        className="bg-slate-950 border-slate-800 text-slate-100"
                        data-testid="new-user-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Email *</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="jean@auto-reprog.fr"
                        className="bg-slate-950 border-slate-800 text-slate-100"
                        data-testid="new-user-email"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Mot de passe *</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        placeholder="••••••••"
                        className="bg-slate-950 border-slate-800 text-slate-100"
                        data-testid="new-user-password"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-slate-300">Rôle *</Label>
                      <Select 
                        value={newUser.role} 
                        onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="new-user-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                          <SelectItem value="admin" className="text-slate-200">Admin</SelectItem>
                          <SelectItem value="manager" className="text-slate-200">Manager</SelectItem>
                          <SelectItem value="mecano" className="text-slate-200">Mécano</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {newUser.role === "manager" && (
                      <div className="space-y-2">
                        <Label className="text-slate-300">Agence assignée</Label>
                        <Select 
                          value={newUser.agency_id} 
                          onValueChange={(v) => setNewUser({ ...newUser, agency_id: v })}
                        >
                          <SelectTrigger className="bg-slate-950 border-slate-800 text-slate-200" data-testid="new-user-agency">
                            <SelectValue placeholder="Sélectionner une agence" />
                          </SelectTrigger>
                          <SelectContent style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
                            {agencies.map((agency) => (
                              <SelectItem key={agency.id} value={agency.id} className="text-slate-200">
                                {agency.city}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="flex gap-3 pt-4">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowAddUserDialog(false)}
                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                      >
                        Annuler
                      </Button>
                      <Button 
                        onClick={handleCreateUser}
                        className="flex-1 btn-primary"
                        data-testid="confirm-new-user-btn"
                      >
                        Créer
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-800">
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <div key={i} className="p-4">
                    <div className="h-12 bg-slate-800 rounded skeleton" />
                  </div>
                ))
              ) : users.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun utilisateur</p>
                </div>
              ) : (
                users.map((u) => (
                  <div 
                    key={u.id} 
                    className="p-4 flex items-center justify-between table-row-hover"
                    data-testid={`user-${u.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        u.role === "admin" ? "bg-purple-600/20" : u.role === "mecano" ? "bg-orange-600/20" : "bg-green-600/20"
                      }`}>
                        <User className={`w-5 h-5 ${
                          u.role === "admin" ? "text-purple-400" : u.role === "mecano" ? "text-orange-400" : "text-green-400"
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-white">{u.name}</p>
                          <Badge 
                            variant="outline" 
                            className={
                              u.role === "admin" 
                                ? "border-purple-500/30 text-purple-400 text-xs" 
                                : u.role === "mecano"
                                  ? "border-orange-500/30 text-orange-400 text-xs"
                                  : "border-green-500/30 text-green-400 text-xs"
                            }
                          >
                            {u.role}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {[...(u.agency_id ? [u.agency_id] : []), ...(u.role === "associe" ? (u.agency_ids || []) : [])].map((aid) => {
                        const color = getAgencyColorByAgency(agencies.find(a => a.id === aid));
                        return (
                          <Badge key={aid} variant="outline" data-testid={`user-agency-${u.id}-${aid}`} className="gap-1.5 hidden sm:inline-flex" style={{ borderColor: color.border, color: color.text, background: color.bg }}>
                            <AgencyDot color={color} className="w-2 h-2" />
                            {getAgencyName(aid)}
                          </Badge>
                        );
                      })}
                      {u.id !== user?.id && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingUser({ ...u });
                              setShowEditUserDialog(true);
                            }}
                            className="text-slate-400 hover:text-[#FEED00] hover:bg-[#FEED00]/10"
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUser(u.id)}
                            className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Twilio Configuration Status */}
      <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }}>
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Settings className="w-5 h-5 text-[#FEED00]" />
            CONFIGURATION SMS (TWILIO)
          </CardTitle>
          <CardDescription className="text-slate-400">
            Rappels SMS automatiques 24h avant les rendez-vous
          </CardDescription>
        </CardHeader>
        <CardContent>
          {smsStatus?.configured ? (
            <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <p className="text-green-400 font-medium">SMS Actif</p>
              </div>
              <div className="space-y-2 text-sm">
                <p className="text-slate-300">
                  <span className="text-slate-500">Numéro d'envoi :</span> {smsStatus.phone_number}
                </p>
                <p className="text-slate-300">
                  <span className="text-slate-500">Statut :</span> {smsStatus.message}
                </p>
              </div>
              <div className="mt-4 p-3 bg-slate-800/50 rounded text-xs text-slate-400">
                <p className="font-medium text-slate-300 mb-1">📱 Fonctionnement :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>SMS envoyés automatiquement à 9h, 14h et 18h pour les RDV du lendemain</li>
                  <li>Bouton "Envoyer SMS" disponible dans les détails de chaque RDV</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <p className="text-red-400 font-medium">SMS Non configuré</p>
              </div>
              <p className="text-slate-300 text-sm mb-3">
                Pour activer l'envoi de SMS, ajoutez les variables suivantes dans le fichier .env du backend :
              </p>
              <div className="bg-slate-950 p-3 rounded font-mono text-xs text-slate-400 space-y-1">
                <p>TWILIO_ACCOUNT_SID=votre_account_sid</p>
                <p>TWILIO_AUTH_TOKEN=votre_auth_token</p>
                <p>TWILIO_PHONE_NUMBER=+33xxxxxxxxx</p>
              </div>
              <p className="text-slate-500 text-xs mt-3">
                Créez un compte sur <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="text-[#FEED00] hover:underline">twilio.com</a> pour obtenir ces identifiants.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meta Lead Ads */}
      <Card style={{ background: '#0A0A0A', borderColor: '#1A1A1A' }} data-testid="meta-settings-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Settings className="w-5 h-5 text-[#FEED00]" />
            META LEAD ADS (FACEBOOK / INSTAGRAM)
          </CardTitle>
          <CardDescription className="text-slate-400">
            Les leads de vos campagnes tombent automatiquement dans le CRM, triés par centre
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className={`p-4 rounded-lg border ${metaStatus?.token_valid ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${metaStatus?.token_valid ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
              <p className={`font-medium ${metaStatus?.token_valid ? 'text-green-400' : 'text-red-400'}`} data-testid="meta-token-status">
                {metaStatus?.token_valid
                  ? `Connexion Meta active${metaStatus.token_name ? ` — ${metaStatus.token_name}` : ''}`
                  : metaStatus?.has_page_token ? 'Token Meta invalide' : 'Token Meta non configuré'}
              </p>
            </div>
            {!metaStatus?.token_valid && metaStatus?.token_error && (
              <p className="text-xs text-red-300/80 mt-2 font-mono break-all">{metaStatus.token_error}</p>
            )}
            <div className="grid sm:grid-cols-3 gap-3 mt-4 text-sm">
              <div className="p-3 rounded bg-black/40">
                <p className="text-slate-500 text-xs">Leads Meta reçus</p>
                <p className="text-white text-xl font-bold" data-testid="meta-leads-count">{metaStatus?.meta_leads_count ?? 0}</p>
              </div>
              <div className="p-3 rounded bg-black/40">
                <p className="text-slate-500 text-xs">Leads à attribuer</p>
                <p className={`text-xl font-bold ${metaStatus?.unassigned_leads_count ? 'text-amber-400' : 'text-white'}`} data-testid="meta-unassigned-count">{metaStatus?.unassigned_leads_count ?? 0}</p>
              </div>
              <div className="p-3 rounded bg-black/40">
                <p className="text-slate-500 text-xs">Centres reliés à une page</p>
                <p className="text-white text-xl font-bold" data-testid="meta-mapped-count">{agencies.filter(a => a.meta_page_id).length}/{agencies.length}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-300">Routage par centre</p>
            <div className="divide-y divide-slate-800 border border-slate-800 rounded-lg overflow-hidden">
              {agencies.map((agency) => {
                const color = getAgencyColorByAgency(agency);
                return (
                  <div key={agency.id} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`meta-route-${agency.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <AgencyDot color={color} />
                      <span className="text-white text-sm font-medium">{agency.city}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {agency.meta_page_id ? (
                        <Badge variant="outline" className="font-mono" style={{ borderColor: color.border, color: color.text }}>
                          Page {metaPages.find(p => p.id === agency.meta_page_id)?.name || agency.meta_page_id}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-amber-500/40 text-amber-400">Aucune page reliée</Badge>
                      )}
                      {agency.meta_form_ids?.length > 0 && (
                        <Badge variant="outline" className="border-slate-700 text-slate-400">{agency.meta_form_ids.length} formulaire(s)</Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 text-slate-400 hover:text-[#FEED00]"
                        onClick={() => { setEditingAgency({ ...agency }); setShowEditAgencyDialog(true); }}
                        data-testid={`meta-route-edit-${agency.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={simulating || !agency.meta_page_id}
                        className="h-7 px-2 border-slate-700 text-slate-300"
                        onClick={async () => {
                          setSimulating(true);
                          try {
                            const r = await axios.post(`${API}/meta/simulate-lead`, { page_id: agency.meta_page_id, name: `Test ${agency.city}` });
                            toast.success(`Lead test créé → ${r.data.agency_city}`);
                            fetchData();
                          } catch { toast.error("Échec du test"); } finally { setSimulating(false); }
                        }}
                        data-testid={`meta-route-test-${agency.id}`}
                      >
                        Tester
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-500">Un lead venant d'une page ou d'un formulaire non relié arrive en « À attribuer » dans l'onglet Leads (visible par les admins).</p>
          </div>

          <div className="p-4 rounded-lg bg-slate-800/40 border border-slate-700/50 space-y-2 text-xs text-slate-400">
            <p className="font-medium text-slate-300">Configuration côté Meta (app Facebook → Webhooks → Page → leadgen) :</p>
            <p><span className="text-slate-500">URL de rappel :</span> <code className="text-[#FEED00] break-all" data-testid="meta-webhook-url">{process.env.REACT_APP_BACKEND_URL}{metaStatus?.webhook_url || '/api/webhooks/meta'}</code></p>
            {metaStatus?.verify_token && (
              <p><span className="text-slate-500">Jeton de vérification :</span> <code className="text-[#FEED00]" data-testid="meta-verify-token">{metaStatus.verify_token}</code></p>
            )}
            <p className="text-slate-500">Chaque page Facebook de centre doit être abonnée à l'app avec la permission <code>leads_retrieval</code>.</p>
          </div>
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              MODIFIER L'UTILISATEUR
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Modifier le rôle et l'agence de l'utilisateur
            </DialogDescription>
          </DialogHeader>
          
          {editingUser && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Nom</Label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input
                  value={editingUser.email}
                  disabled
                  className="bg-slate-800 border-slate-700 text-slate-500"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Rôle *</Label>
                <Select 
                  value={editingUser.role} 
                  onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="admin" className="text-white">Admin (toutes les agences)</SelectItem>
                    <SelectItem value="associe" className="text-white">Associé (plusieurs agences)</SelectItem>
                    <SelectItem value="manager" className="text-white">Manager (une agence)</SelectItem>
                    <SelectItem value="mecano" className="text-white">Mécano (planning uniquement)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(editingUser.role === "manager" || editingUser.role === "mecano") && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Agence *</Label>
                  <Select 
                    value={editingUser.agency_id || ""} 
                    onValueChange={(value) => setEditingUser({ ...editingUser, agency_id: value })}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Sélectionner une agence" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id} className="text-white">
                          {agency.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {editingUser.role === "associe" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Agences de l'associé * <span className="text-slate-500 text-xs">(plusieurs choix possibles)</span></Label>
                  <div className="border border-slate-700 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 bg-slate-800">
                    {agencies.map((agency) => {
                      const isChecked = editingUser.agency_ids?.includes(agency.id) || false;
                      return (
                        <label 
                          key={agency.id} 
                          className="flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-slate-700 transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              let newAgencyIds = editingUser.agency_ids || [];
                              if (e.target.checked) {
                                newAgencyIds = [...newAgencyIds, agency.id];
                              } else {
                                newAgencyIds = newAgencyIds.filter(id => id !== agency.id);
                              }
                              setEditingUser({ ...editingUser, agency_ids: newAgencyIds });
                            }}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-[#FEED00] focus:ring-[#FEED00]"
                          />
                          <span className="text-white text-sm">{agency.city}</span>
                        </label>
                      );
                    })}
                  </div>
                  {editingUser.agency_ids && editingUser.agency_ids.length > 0 && (
                    <p className="text-xs text-[#FEED00]">
                      Sélectionné : {editingUser.agency_ids.map(id => agencies.find(a => a.id === id)?.city).filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300"
                  onClick={() => {
                    setShowEditUserDialog(false);
                    setEditingUser(null);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  className="flex-1 btn-primary"
                  onClick={handleUpdateUser}
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
